import { useTenantScope, normalizeScopePlatform } from "./scope/TenantScopeContext";
import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api/client";
import SecurityAlertsPanel from "./SecurityAlertsPanel";
import BlockedIpsPanel from "./BlockedIpsPanel";
import TrafficSpikePanel from "./TrafficSpikePanel";
import ActiveIpPanel from "./ActiveIpPanel";
import NetworkIntelligencePanel from "./NetworkIntelligencePanel";


function classifyRuntimeSeverity(connection) {
  const localPort = Number(connection?.local_port || 0);
  const remotePort = Number(connection?.remote_port || 0);
  const port = remotePort || localPort;

  if ([22, 23, 3389, 5900, 5901].includes(port)) return "High";
  if ([135, 137, 138, 139, 445, 1433, 3306, 5432].includes(port)) {
    return "Medium";
  }

  return "Low";
}

function normalizeRuntimeConnections(detail) {
  const connections = Array.isArray(detail?.network_connections)
    ? detail.network_connections
    : [];

  const observedAt =
    detail?.observed_at || detail?.created_at || new Date().toISOString();

  return connections.map((connection, index) => {
    const protocol = String(connection?.protocol || "TCP").toUpperCase();
    const localIp = connection?.local_address || connection?.local_ip || "";
    const remoteIp = connection?.remote_address || connection?.remote_ip || "";
    const localPort = connection?.local_port ?? "";
    const remotePort = connection?.remote_port ?? "";
    const state = String(connection?.state || connection?.status || "").toUpperCase();
    const processName = connection?.process_name || "Unknown process";

    return {
      id: `runtime-${detail?.device_uuid || "device"}-${index}`,
      device_uuid: detail?.device_uuid || "",
      hostname: detail?.device_name || detail?.hostname || "Protected Endpoint",
      local_ip: localIp,
      local_port: String(localPort),
      remote_ip: remoteIp,
      remote_port: String(remotePort),
      protocol,
      status: state,
      process_name: processName,
      severity: classifyRuntimeSeverity(connection),
      event_type: "Runtime Network Connection",
      message: `${protocol} ${localIp || "local"}:${localPort || "-"} → ${remoteIp || "remote"}:${remotePort || "-"} ${state}`.trim(),
      created_at: observedAt,
    };
  });
}

function buildRuntimeSummary(events, detail) {
  const high = events.filter((event) => event.severity === "High").length;
  const medium = events.filter((event) => event.severity === "Medium").length;
  const low = events.filter((event) => event.severity === "Low").length;

  const listenerStates = new Set(["LISTEN", "LISTENING"]);
  const sensitive = events
    .filter(
      (event) =>
        listenerStates.has(String(event.status || "").toUpperCase()) &&
        ["High", "Medium"].includes(event.severity)
    )
    .slice(0, 10);

  const grouped = new Map();

  events
    .filter((event) => event.remote_ip)
    .forEach((event) => {
      const key = [
        event.remote_ip,
        event.remote_port,
        event.process_name,
        event.severity,
      ].join("|");

      const current = grouped.get(key) || {
        remote_ip: event.remote_ip,
        remote_port: event.remote_port,
        process_name: event.process_name,
        severity: event.severity,
        connection_count: 0,
      };

      current.connection_count += 1;
      grouped.set(key, current);
    });

  const topRemote = [...grouped.values()]
    .sort((a, b) => b.connection_count - a.connection_count)
    .slice(0, 10);

  return {
    total_events_24h: events.length,
    high_alerts_24h: high,
    medium_alerts_24h: medium,
    low_events_24h: low,
    reporting_devices_24h: detail?.observed_at ? 1 : 0,
    sensitive_ports_open_24h: sensitive.length,
    top_remote_connections: topRemote,
    sensitive_listening_services: sensitive,
  };
}

function firewallLabel(value) {
  if (!value || typeof value !== "object") return "Not reported";
  if (value.enabled === true) return "Enabled";
  if (value.enabled === false) return "Disabled";
  if (value.status) return String(value.status);
  return "Reported";
}

function NetworkProtection() {
  const globalScope = useTenantScope();
  const role = localStorage.getItem("role") || "client_admin";
  const sessionCompanyId = localStorage.getItem("company_id") || "";

  const companyId =
    role === "super_admin"
      ? String(globalScope.companyId || "")
      : String(sessionCompanyId || "");

  const [devices, setDevices] = useState([]);
  const [selectedDeviceUuid, setSelectedDeviceUuid] = useState("");

  // R12I_NETWORK_GLOBAL_DEVICE_SYNC
  useEffect(() => {
    if (
      globalScope.deviceUuid &&
      globalScope.deviceUuid !== selectedDeviceUuid
    ) {
      setSelectedDeviceUuid(globalScope.deviceUuid);
    }
  }, [globalScope.deviceUuid, selectedDeviceUuid]);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [canonicalDetections, setCanonicalDetections] = useState([]);
  const [canonicalDetectionError, setCanonicalDetectionError] = useState("");
  const [canonicalDetectionLoading, setCanonicalDetectionLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sourceObservedAt, setSourceObservedAt] = useState(null);
  const [dataSource, setDataSource] = useState("runtime-pending");
  const [firewallState, setFirewallState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const runtimeCompanySuffix =
    companyId
      ? `?company_id=${encodeURIComponent(companyId)}`
      : "";

  const loadManagedDevices = async () => {
    try {
      setError("");

      if (!companyId) {
        setDevices([]);
        setSelectedDeviceUuid("");
        setSummary(buildRuntimeSummary([], null));
        setEvents([]);
        setDataSource("no-company-scope");
        setSourceObservedAt(null);
        setFirewallState(null);
        setLoading(false);
        return;
      }

      const path =
        `/devices/company/${encodeURIComponent(companyId)}`;

      const response = await apiFetch(path);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.detail || "Unable to load managed endpoints");
      }

      let rows = Array.isArray(data) ? data : [];

      rows = rows
        .filter(
          (device) =>
            String(device.company_id || "") === String(companyId) &&
            device.device_uuid &&
            String(device.lifecycle_state || "active").toLowerCase() === "active"
        )
        .sort((a, b) => {
          const aOnline = String(a.status || "").toLowerCase() === "online" ? 1 : 0;
          const bOnline = String(b.status || "").toLowerCase() === "online" ? 1 : 0;

          if (aOnline !== bOnline) return bOnline - aOnline;

          return (
            new Date(b.last_seen || 0).getTime() -
            new Date(a.last_seen || 0).getTime()
          );
        });

      const platformScopedRows =
        !globalScope.platform ||
        globalScope.platform === "all"
          ? rows
          : rows.filter(
              (device) =>
                normalizeScopePlatform(device.platform) ===
                globalScope.platform
            );

      setDevices(platformScopedRows);
      setSelectedDeviceUuid((current) => {
        if (
          current &&
          platformScopedRows.some(
            (device) => device.device_uuid === current
          )
        ) {
          return current;
        }

        return platformScopedRows[0]?.device_uuid || "";
      });

      if (platformScopedRows.length === 0) {
        setSummary(buildRuntimeSummary([], null));
        setEvents([]);
        setDataSource("no-active-endpoint");
        setSourceObservedAt(null);
        setFirewallState(null);
        setLoading(false);
      }
    } catch (loadError) {
      console.error("Network endpoint list error:", loadError);
      setError(loadError.message || "Unable to load managed endpoints");
      setDevices([]);
      setSelectedDeviceUuid("");
      setSummary(buildRuntimeSummary([], null));
      setEvents([]);
      setDataSource("device-list-error");
      setLoading(false);
    }
  };

  const loadCanonicalDetections = async () => {
    try {
      setCanonicalDetectionError("");
      setCanonicalDetectionLoading(true);

      if (!companyId) {
        setCanonicalDetections([]);
        setCanonicalDetectionLoading(false);
        return;
      }

      const suffix =
        `?company_id=${encodeURIComponent(companyId)}&status=open&limit=500`;

      const response = await apiFetch(
        `/network-protection/detections${suffix}`
      );

      let data = null;

      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            `Unable to load canonical Network Protection detections (HTTP ${response.status}).`
        );
      }

      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.detections)
          ? data.detections
          : [];

      setCanonicalDetections(rows);
    } catch (detectionError) {
      console.error(
        "Canonical Network Protection detection error:",
        detectionError
      );

      setCanonicalDetectionError(
        detectionError?.message ||
          "Unable to load canonical Network Protection detections"
      );

      setCanonicalDetections([]);
    } finally {
      setCanonicalDetectionLoading(false);
    }
  };

  const fetchNetworkData = async () => {
    if (!selectedDeviceUuid) return;

    try {
      setError("");

      const response = await apiFetch(
        `/runtime-telemetry/devices/${encodeURIComponent(
          selectedDeviceUuid
        )}${runtimeCompanySuffix}`
      );

      let detail = null;

      try {
        detail = await response.json();
      } catch {
        detail = null;
      }

      if (!response.ok) {
        setEvents([]);
        setSummary(buildRuntimeSummary([], null));
        setSourceObservedAt(null);
        setFirewallState(null);
        setDataSource("runtime-unavailable");

        throw new Error(
          detail?.detail ||
            `No current runtime telemetry is available for this endpoint (HTTP ${response.status}).`
        );
      }

      const normalized = normalizeRuntimeConnections(detail);

      setEvents(normalized);
      setSummary(buildRuntimeSummary(normalized, detail));
      setSourceObservedAt(detail?.observed_at || detail?.created_at || null);
      setFirewallState(detail?.firewall_state || null);
      setDataSource("runtime-current");
      setLastUpdated(new Date());
    } catch (networkError) {
      console.error("Device-scoped network telemetry error:", networkError);
      setError(
        networkError.message ||
          "Unable to load current device-scoped network telemetry"
      );
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadManagedDevices();
    loadCanonicalDetections();

    const detectionTimer = window.setInterval(() => {
      loadCanonicalDetections();
    }, 30000);

    return () => window.clearInterval(detectionTimer);
  }, [companyId, role]);

  useEffect(() => {
    if (!selectedDeviceUuid) return undefined;

    setLoading(true);
    fetchNetworkData();

    const interval = window.setInterval(() => {
      fetchNetworkData();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [selectedDeviceUuid]);

  const selectedDevice = useMemo(
    () =>
      devices.find((device) => device.device_uuid === selectedDeviceUuid) || null,
    [devices, selectedDeviceUuid]
  );

  const scopedCanonicalDetections = useMemo(() => {
    const allowedDeviceUuids = new Set(
      devices
        .map((device) => String(device.device_uuid || ""))
        .filter(Boolean)
    );

    return canonicalDetections.filter((detection) => {
      const deviceUuid = String(
        detection?.device_uuid || ""
      );

      if (
        allowedDeviceUuids.size > 0 &&
        !allowedDeviceUuids.has(deviceUuid)
      ) {
        return false;
      }

      if (
        globalScope.deviceUuid &&
        deviceUuid !== String(globalScope.deviceUuid)
      ) {
        return false;
      }

      return true;
    });
  }, [
    canonicalDetections,
    devices,
    globalScope.deviceUuid,
  ]);

  const selectedDeviceDetections = useMemo(
    () =>
      scopedCanonicalDetections.filter(
        (detection) =>
          !selectedDeviceUuid ||
          String(detection?.device_uuid || "") ===
            String(selectedDeviceUuid)
      ),
    [
      scopedCanonicalDetections,
      selectedDeviceUuid,
    ]
  );

  const trafficTrend = useMemo(() => buildTrafficTrend(events), [events]);
  const portChart = useMemo(
    () => buildPortChart(summary, events),
    [summary, events]
  );

  const activeDevice =
    selectedDevice?.hostname ||
    selectedDevice?.device_name ||
    events[0]?.hostname ||
    "Protected Endpoint";

  const highestRisk = getHighestRisk(summary);

  if (loading) {
    return (
      <div className="cm-network-page">
        <div className="loading-card">
          <div className="loading-pulse"></div>
          <h2>Loading Congo Market AI Network Intelligence...</h2>
          <p>Collecting live network telemetry, open ports, and traffic alerts.</p>
        </div>
        <NetworkStyles />
      </div>
    );
  }

  return (
    <div className="cm-network-page">
      <section className="cm-hero">
        <div>
          <div className="cm-pill">Congo Market AI • Network Intelligence</div>
          <h1>Network Protection Center</h1>
          <p>
            A centralized graphical interface for visualizing real-time network
            traffic, exposed services, suspicious ports, remote connections, and
            endpoint activity collected by the Wireless Press Lockdown agent.
          </p>
        </div>

        <div className="status-card">
          <span className={`risk-badge ${highestRisk.toLowerCase()}`}>
            {highestRisk} Risk
          </span>

          <div>
            <h2>{summary?.reporting_devices_24h || 0}</h2>
            <p>Reporting device(s)</p>
          </div>

          <button
            onClick={() => {
              fetchNetworkData();
              loadCanonicalDetections();
            }}
          >
            Refresh
          </button>
        </div>
      </section>

      <section className="panel" style={{ marginBottom: "20px" }}>
        <PanelHeader
          title="Endpoint Network Scope"
          subtitle="Every network visualization below is bound to one managed endpoint. Current runtime telemetry is used so stale global history cannot appear as live data."
          live={dataSource === "runtime-current"}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(260px, 1fr) repeat(3, minmax(150px, auto))",
            gap: "12px",
            alignItems: "end",
          }}
        >
          <label style={{ display: "grid", gap: "6px", fontWeight: 700 }}>
            Managed endpoint
            <select
              value={selectedDeviceUuid}
              onChange={(event) => setSelectedDeviceUuid(event.target.value)}
              style={{ padding: "10px 12px", borderRadius: "10px" }}
            >
              {devices.map((device) => (
                <option key={device.device_uuid} value={device.device_uuid}>
                  {device.hostname || device.device_name || "Unnamed endpoint"}
                  {` · ${device.platform || "Unknown OS"} · ${device.status || "unknown"}`}
                </option>
              ))}
            </select>
          </label>

          <div>
            <strong>Source</strong>
            <div>
              {dataSource === "runtime-current"
                ? "Current runtime telemetry"
                : dataSource === "runtime-unavailable"
                  ? "No current runtime snapshot"
                  : "Waiting for endpoint telemetry"}
            </div>
          </div>

          <div>
            <strong>Observed</strong>
            <div>
              {sourceObservedAt
                ? new Date(sourceObservedAt).toLocaleString()
                : "Not reported"}
            </div>
          </div>

          <div>
            <strong>Firewall</strong>
            <div>{firewallLabel(firewallState)}</div>
          </div>
        </div>

        {dataSource === "runtime-unavailable" && (
          <div className="error-box" style={{ marginTop: "14px" }}>
            No current network snapshot exists for this endpoint. Historical
            global network_events data is intentionally not substituted.
          </div>
        )}
      </section>

      {error && (
        <div className="error-box">
          <strong>Error:</strong> {error}
        </div>
      )}

      <section className="metrics-grid">
        <MetricCard
          title="Network Events"
          value={summary?.total_events_24h || 0}
          label={dataSource === "runtime-current" ? "Current endpoint snapshot" : "No current snapshot"}
          tone="blue"
        />

        <MetricCard
          title="High Alerts"
          value={summary?.high_alerts_24h || 0}
          label="RDP, SSH, VNC, high-risk ports"
          tone="red"
        />

        <MetricCard
          title="Medium Alerts"
          value={summary?.medium_alerts_24h || 0}
          label="SMB, RPC, NetBIOS, database ports"
          tone="amber"
        />

        <MetricCard
          title="Sensitive Services"
          value={summary?.sensitive_ports_open_24h || 0}
          label="Open listening ports"
          tone="purple"
        />

        <MetricCard
          title="Low Events"
          value={summary?.low_events_24h || 0}
          label="Normal web/background traffic"
          tone="green"
        />
      </section>

      <SecurityAlertsPanel deviceUuid={selectedDeviceUuid} />

      <NetworkIntelligencePanel
        companyId={companyId}
        deviceUuid={selectedDeviceUuid}
      />

      <CanonicalNetworkDetections
        detections={selectedDeviceDetections}
        loading={canonicalDetectionLoading}
        error={canonicalDetectionError}
        selectedDevice={selectedDevice}
        companyId={companyId}
        onReload={loadCanonicalDetections}
      />

      <BlockedIpsPanel
        companyId={companyId}
        role={role}
        allowedDeviceUuids={devices.map(
          (device) => device.device_uuid
        )}
      />

      <TrafficSpikePanel events={events} onRefresh={fetchNetworkData} />
      <ActiveIpPanel events={events} onRefresh={fetchNetworkData} />

      <section className="visual-grid-main">
        <div className="panel network-map-panel">
          <PanelHeader
            title="Live Network Visualization"
            subtitle="Central endpoint view showing active services, remote destinations, and severity paths."
            live
          />

          <NetworkMap
            activeDevice={activeDevice}
            services={summary?.sensitive_listening_services || []}
            remotes={summary?.top_remote_connections || []}
          />
        </div>

        <div className="panel">
          <PanelHeader
            title="Severity Distribution"
            subtitle="Real-time breakdown of event severity over the last 24 hours."
          />

          <SeverityDonut summary={summary} />

          <div className="severity-legend">
            <LegendItem
              label="High"
              value={summary?.high_alerts_24h || 0}
              tone="red"
            />
            <LegendItem
              label="Medium"
              value={summary?.medium_alerts_24h || 0}
              tone="amber"
            />
            <LegendItem
              label="Low"
              value={summary?.low_events_24h || 0}
              tone="green"
            />
          </div>

          <div className="last-updated">
            Last updated: {lastUpdated ? lastUpdated.toLocaleString() : "N/A"}
          </div>
        </div>
      </section>

      <section className="visual-grid-two">
        <div className="panel">
          <PanelHeader
            title="Traffic Activity Trend"
            subtitle="Recent event volume grouped into live collection windows."
          />
          <TrafficLineChart data={trafficTrend} />
        </div>

        <div className="panel">
          <PanelHeader
            title="Port Exposure Graph"
            subtitle="Most visible local/remote ports from recent network activity."
          />
          <PortBarChart data={portChart} />
        </div>
      </section>

      <section className="visual-grid-two">
        <div className="panel">
          <PanelHeader
            title="Sensitive Listening Services"
            subtitle="Open services on protected devices that should be reviewed."
          />
          <SensitiveServices
            services={summary?.sensitive_listening_services || []}
          />
        </div>

        <div className="panel">
          <PanelHeader
            title="Top Remote Connections"
            subtitle="Most frequent remote destinations observed by the agent."
          />
          <RemoteConnections connections={summary?.top_remote_connections || []} />
        </div>
      </section>

      <section className="panel">
        <PanelHeader
          title="Live Event Stream"
          subtitle="Latest network telemetry received from the endpoint agent."
        />
        <EventTable events={events} />
      </section>

      <NetworkStyles />
    </div>
  );
}

function CanonicalNetworkDetections({
  detections,
  loading,
  error,
  selectedDevice,
  companyId,
  onReload,
}) {
  const rows = Array.isArray(detections)
    ? detections
    : [];

  const [resolutionTarget, setResolutionTarget] = useState(null);
  const [resolutionReason, setResolutionReason] = useState("");
  const [resolutionBusy, setResolutionBusy] = useState(false);
  const [resolutionError, setResolutionError] = useState("");
  const [resolutionNotice, setResolutionNotice] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const selectedDeviceUuid =
    selectedDevice?.device_uuid || "";

  const withCompany = (path) => {
    if (!companyId) return path;

    const separator = path.includes("?")
      ? "&"
      : "?";

    return `${path}${separator}company_id=${encodeURIComponent(
      companyId
    )}`;
  };

  const loadResolutionHistory = async () => {
    if (!selectedDeviceUuid) {
      setHistory([]);
      return;
    }

    setHistoryLoading(true);
    setHistoryError("");

    try {
      const path = withCompany(
        `/network-protection/resolution-history?device_uuid=${encodeURIComponent(
          selectedDeviceUuid
        )}&limit=200`
      );

      const response = await apiFetch(path);

      if (!response.ok) {
        let detail = "";

        try {
          const body = await response.json();
          detail = body?.detail
            ? `: ${body.detail}`
            : "";
        } catch (_) {}

        throw new Error(
          `Unable to load resolution history (HTTP ${response.status})${detail}`
        );
      }

      const data = await response.json();

      setHistory(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (historyLoadError) {
      console.error(
        "Network Protection resolution history error:",
        historyLoadError
      );

      setHistory([]);

      setHistoryError(
        historyLoadError?.message ||
          "Unable to load resolution history"
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadResolutionHistory();
    // selected endpoint / tenant change intentionally reloads history
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceUuid, companyId]);

  const beginResolution = (item) => {
    setResolutionTarget(item);
    setResolutionReason("");
    setResolutionError("");
    setResolutionNotice("");
  };

  const cancelResolution = () => {
    if (resolutionBusy) return;

    setResolutionTarget(null);
    setResolutionReason("");
    setResolutionError("");
  };

  const resolveFinding = async () => {
    const reason = String(
      resolutionReason || ""
    ).trim();

    if (!resolutionTarget?.detection_uuid) {
      setResolutionError(
        "No Network Protection finding is selected."
      );
      return;
    }

    if (reason.length < 3) {
      setResolutionError(
        "Resolution reason must contain at least 3 characters."
      );
      return;
    }

    if (reason.length > 2000) {
      setResolutionError(
        "Resolution reason cannot exceed 2000 characters."
      );
      return;
    }

    setResolutionBusy(true);
    setResolutionError("");
    setResolutionNotice("");

    try {
      const path = withCompany(
        `/network-protection/detections/${encodeURIComponent(
          resolutionTarget.detection_uuid
        )}/resolve`
      );

      const response = await apiFetch(
        path,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason,
          }),
        }
      );

      let body = {};

      try {
        body = await response.json();
      } catch (_) {}

      if (!response.ok) {
        throw new Error(
          body?.detail ||
            `Unable to resolve finding (HTTP ${response.status})`
        );
      }

      if (
        body?.status !== "resolved" ||
        body?.audit_recorded !== true
      ) {
        throw new Error(
          "Resolution response did not confirm durable audit recording."
        );
      }

      setResolutionNotice(
        "Finding resolved and immutable audit history recorded."
      );

      setResolutionTarget(null);
      setResolutionReason("");

      if (typeof onReload === "function") {
        await onReload();
      }

      await loadResolutionHistory();
    } catch (resolveError) {
      console.error(
        "Network Protection finding resolution error:",
        resolveError
      );

      setResolutionError(
        resolveError?.message ||
          "Unable to resolve Network Protection finding"
      );
    } finally {
      setResolutionBusy(false);
    }
  };

  const titleForType = (value) => {
    const type = String(value || "");

    if (type === "high_risk_listening_service") {
      return "High-Risk Listening Service";
    }

    if (type === "firewall_disabled") {
      return "Endpoint Firewall Disabled";
    }

    if (type === "suspicious_remote_connection") {
      return "Suspicious Remote Connection";
    }

    if (type === "brute_force_login") {
      return "Possible Brute Force Login";
    }

    return type
      ? type.replaceAll("_", " ")
      : "Network Detection";
  };

  return (
    <section className="panel canonical-detection-panel">
      <PanelHeader
        title="Canonical Network Protection Findings"
        subtitle="Persisted Network Protection detections are reviewed here. Resolving a finding records an immutable administrative audit event and does not create a firewall block."
        live
      />

      <div className="canonical-detection-scope">
        <span>Endpoint</span>
        <strong>
          {selectedDevice?.hostname ||
            selectedDevice?.device_name ||
            selectedDevice?.device_uuid ||
            "No endpoint selected"}
        </strong>

        <span>Open findings</span>
        <strong>{rows.length}</strong>

        <span>Resolution history</span>
        <strong>{history.length}</strong>
      </div>

      {error && (
        <div className="error-box">
          <strong>Error:</strong> {error}
        </div>
      )}

      {resolutionError && (
        <div
          className="error-box"
          style={{ marginTop: 12 }}
        >
          <strong>Resolution error:</strong>{" "}
          {resolutionError}
        </div>
      )}

      {resolutionNotice && (
        <div
          style={{
            marginTop: 12,
            padding: "12px 14px",
            border: "1px solid #235f4a",
            borderRadius: 10,
            background: "#102b23",
            color: "#9ce4c8",
          }}
        >
          {resolutionNotice}
        </div>
      )}

      {loading ? (
        <div className="empty-state">
          Loading canonical Network Protection findings...
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          No open canonical Network Protection findings are currently
          persisted for this endpoint.
        </div>
      ) : (
        <div className="event-table-wrap">
          <table className="event-table canonical-detection-table">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Finding</th>
                <th>Status</th>
                <th>Destination</th>
                <th>Process</th>
                <th>First Seen</th>
                <th>Last Seen</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((item) => (
                <tr key={item.detection_uuid || item.id}>
                  <td>
                    <span
                      className={`tag ${String(
                        item.severity || "low"
                      ).toLowerCase()}`}
                    >
                      {String(
                        item.severity || "unknown"
                      ).toUpperCase()}
                    </span>
                  </td>

                  <td>
                    <strong>
                      {item.title ||
                        titleForType(item.detection_type)}
                    </strong>

                    <div className="canonical-detection-type">
                      {item.detection_type || "N/A"}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        color: "#7890a4",
                      }}
                    >
                      {item.detection_uuid}
                    </div>
                  </td>

                  <td>
                    {String(
                      item.status || "unknown"
                    ).replaceAll("_", " ")}
                  </td>

                  <td>
                    {item.detection_type === "firewall_disabled"
                      ? "Endpoint firewall"
                      : `${item.destination_address || "N/A"}${
                          item.destination_port
                            ? `:${item.destination_port}`
                            : ""
                        }`}
                  </td>

                  <td>{item.process_name || "N/A"}</td>

                  <td>{formatDate(item.first_seen_at)}</td>

                  <td>{formatDate(item.last_seen_at)}</td>

                  <td>
                    <button
                      type="button"
                      disabled={
                        resolutionBusy ||
                        item.status !== "open"
                      }
                      onClick={() => beginResolution(item)}
                      style={{
                        border: "1px solid #2d6b58",
                        borderRadius: 8,
                        background: "#153a30",
                        color: "#b9f3dd",
                        padding: "8px 12px",
                        cursor:
                          resolutionBusy
                            ? "not-allowed"
                            : "pointer",
                        fontWeight: 700,
                      }}
                    >
                      Resolve Finding
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resolutionTarget && (
        <div
          style={{
            marginTop: 18,
            border: "1px solid #33495c",
            borderRadius: 12,
            background: "#101c27",
            padding: 16,
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            Resolve Network Protection Finding
          </h3>

          <p style={{ color: "#9fb3c5" }}>
            <strong>
              {resolutionTarget.title ||
                titleForType(
                  resolutionTarget.detection_type
                )}
            </strong>
            {" · "}
            {resolutionTarget.detection_uuid}
          </p>

          <label
            style={{
              display: "block",
              marginBottom: 8,
              fontWeight: 700,
            }}
          >
            Resolution reason
          </label>

          <textarea
            value={resolutionReason}
            minLength={3}
            maxLength={2000}
            disabled={resolutionBusy}
            onChange={(event) =>
              setResolutionReason(
                event.target.value
              )
            }
            placeholder="Document why this finding is being resolved..."
            style={{
              width: "100%",
              minHeight: 100,
              resize: "vertical",
              boxSizing: "border-box",
              borderRadius: 9,
              border: "1px solid #395166",
              background: "#0b1620",
              color: "#eef5fb",
              padding: 12,
            }}
          />

          <div
            style={{
              marginTop: 6,
              color: "#7890a4",
              fontSize: 12,
            }}
          >
            {resolutionReason.length}/2000 characters
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 12,
            }}
          >
            <button
              type="button"
              disabled={
                resolutionBusy ||
                resolutionReason.trim().length < 3
              }
              onClick={resolveFinding}
              style={{
                border: "1px solid #2d6b58",
                borderRadius: 8,
                background: "#16815d",
                color: "#ffffff",
                padding: "9px 14px",
                fontWeight: 700,
              }}
            >
              {resolutionBusy
                ? "Resolving..."
                : "Confirm Resolution"}
            </button>

            <button
              type="button"
              disabled={resolutionBusy}
              onClick={cancelResolution}
              style={{
                border: "1px solid #44576a",
                borderRadius: 8,
                background: "#172633",
                color: "#d6e2ec",
                padding: "9px 14px",
              }}
            >
              Cancel
            </button>
          </div>

          <p
            style={{
              marginBottom: 0,
              color: "#8da3b5",
              fontSize: 12,
            }}
          >
            Resolving records an immutable administrative audit
            event. It does not approve or execute a network block.
          </p>
        </div>
      )}

      <div
        style={{
          marginTop: 20,
          borderTop: "1px solid #263747",
          paddingTop: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>
              Resolution History
            </h3>

            <p
              style={{
                margin: "5px 0 0",
                color: "#8da3b5",
              }}
            >
              Immutable administrative resolution events for
              this endpoint.
            </p>
          </div>

          <button
            type="button"
            disabled={
              historyLoading ||
              !selectedDeviceUuid
            }
            onClick={loadResolutionHistory}
            style={{
              border: "1px solid #395166",
              borderRadius: 8,
              background: "#172633",
              color: "#d6e2ec",
              padding: "8px 12px",
            }}
          >
            {historyLoading
              ? "Refreshing..."
              : "Refresh History"}
          </button>
        </div>

        {historyError && (
          <div className="error-box">
            <strong>History error:</strong>{" "}
            {historyError}
          </div>
        )}

        {historyLoading ? (
          <div className="empty-state">
            Loading immutable resolution history...
          </div>
        ) : history.length === 0 ? (
          <div className="empty-state">
            No resolution history has been recorded for this
            endpoint.
          </div>
        ) : (
          <div className="event-table-wrap">
            <table className="event-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Finding</th>
                  <th>Transition</th>
                  <th>Administrator</th>
                  <th>Role</th>
                  <th>Reason</th>
                </tr>
              </thead>

              <tbody>
                {history.map((item) => (
                  <tr key={item.audit_uuid}>
                    <td>
                      {formatDate(item.created_at)}
                    </td>

                    <td>
                      <code>
                        {item.detection_uuid}
                      </code>
                    </td>

                    <td>
                      {item.old_status || "N/A"}
                      {" → "}
                      {item.new_status || "N/A"}
                    </td>

                    <td>
                      {item.actor_name || "N/A"}
                    </td>

                    <td>
                      {item.actor_role || "N/A"}
                    </td>

                    <td>
                      {item.reason || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="canonical-detection-note">
        Finding resolution is separate from Network Protection
        response controls. Block requests, approvals, releases,
        and firewall actions remain managed separately below.
      </div>
    </section>
  );
}


function MetricCard({ title, value, label, tone }) {
  return (
    <div className={`metric-card ${tone}`}>
      <div className="metric-head">
        <span>{title}</span>
        <div className={`metric-dot ${tone}`}></div>
      </div>
      <h2>{value}</h2>
      <p>{label}</p>
    </div>
  );
}

function PanelHeader({ title, subtitle, live }) {
  return (
    <div className="panel-header">
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      {live && (
        <div className="live-badge">
          <span></span>
          Live
        </div>
      )}
    </div>
  );
}

function NetworkMap({ activeDevice, services, remotes }) {
  const nodes = [
    ...services.slice(0, 5).map((service) => ({
      label: `Port ${service.local_port}`,
      sub: service.process_name || "Local service",
      severity: service.severity || "Medium",
      type: "Service",
    })),
    ...remotes.slice(0, 5).map((remote) => ({
      label: remote.remote_ip || "Remote host",
      sub: `${remote.process_name || "Unknown"}:${remote.remote_port || ""}`,
      severity: remote.severity || "Low",
      type: "Remote",
    })),
  ].slice(0, 9);

  const positions = [
    { x: 16, y: 18 },
    { x: 48, y: 10 },
    { x: 82, y: 18 },
    { x: 90, y: 52 },
    { x: 76, y: 84 },
    { x: 48, y: 92 },
    { x: 20, y: 82 },
    { x: 8, y: 52 },
    { x: 64, y: 34 },
  ];

  return (
    <div className="network-map">
      <svg className="map-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        {nodes.map((node, index) => {
          const position = positions[index];

          return (
            <line
              key={index}
              x1="50"
              y1="50"
              x2={position.x}
              y2={position.y}
              className={`map-line ${node.severity.toLowerCase()}`}
            />
          );
        })}
      </svg>

      <div className="radar-ring ring-one"></div>
      <div className="radar-ring ring-two"></div>

      <div className="center-device">
        <span>Protected Endpoint</span>
        <strong>{activeDevice}</strong>
      </div>

      {nodes.length === 0 && (
        <div className="empty-map">
          No network nodes detected yet. Run the network agent to populate the
          visualization.
        </div>
      )}

      {nodes.map((node, index) => {
        const position = positions[index];

        return (
          <div
            key={index}
            className={`network-node ${node.severity.toLowerCase()}`}
            style={{
              left: `${position.x}%`,
              top: `${position.y}%`,
            }}
          >
            <span>{node.type}</span>
            <strong>{node.label}</strong>
            <small>{node.sub}</small>
          </div>
        );
      })}
    </div>
  );
}

function SeverityDonut({ summary }) {
  const high = Number(summary?.high_alerts_24h || 0);
  const medium = Number(summary?.medium_alerts_24h || 0);
  const low = Number(summary?.low_events_24h || 0);
  const total = Math.max(high + medium + low, 1);

  const highDeg = (high / total) * 360;
  const mediumDeg = (medium / total) * 360;

  const chartStyle = {
    background: `conic-gradient(
      #ef4444 0deg ${highDeg}deg,
      #f59e0b ${highDeg}deg ${highDeg + mediumDeg}deg,
      #22c55e ${highDeg + mediumDeg}deg 360deg
    )`,
  };

  return (
    <div className="donut-wrap">
      <div className="donut-chart" style={chartStyle}>
        <div className="donut-hole">
          <strong>{total}</strong>
          <span>Total</span>
        </div>
      </div>
    </div>
  );
}

function LegendItem({ label, value, tone }) {
  return (
    <div className="legend-item">
      <span className={`legend-dot ${tone}`}></span>
      <p>{label}</p>
      <strong>{value}</strong>
    </div>
  );
}

function TrafficLineChart({ data }) {
  const max = Math.max(...data.map((item) => item.value), 1);

  const points = data
    .map((item, index) => {
      const x = (index / Math.max(data.length - 1, 1)) * 100;
      const y = 100 - (item.value / max) * 80 - 10;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="line-chart-wrap">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="line-chart">
        <polyline points={points} className="line-path" />
        {data.map((item, index) => {
          const x = (index / Math.max(data.length - 1, 1)) * 100;
          const y = 100 - (item.value / max) * 80 - 10;

          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="1.6"
              className="line-point"
            />
          );
        })}
      </svg>

      <div className="chart-labels">
        {data.map((item, index) => (
          <span key={index}>{item.label}</span>
        ))}
      </div>
    </div>
  );
}

function PortBarChart({ data }) {
  const max = Math.max(...data.map((item) => item.count), 1);

  if (!data.length) {
    return <div className="empty-state">No port activity available.</div>;
  }

  return (
    <div className="bar-list">
      {data.slice(0, 8).map((item, index) => (
        <div className="bar-row" key={index}>
          <div className="bar-label">
            <strong>{item.port}</strong>
            <span>{item.label}</span>
          </div>

          <div className="bar-track">
            <div
              className={`bar-fill ${item.severity.toLowerCase()}`}
              style={{ width: `${Math.max((item.count / max) * 100, 8)}%` }}
            ></div>
          </div>

          <div className="bar-count">{item.count}</div>
        </div>
      ))}
    </div>
  );
}

function SensitiveServices({ services }) {
  if (!services.length) {
    return <div className="empty-state">No sensitive listening services detected.</div>;
  }

  return (
    <div className="service-list">
      {services.map((service, index) => (
        <div className="service-item" key={index}>
          <div className={`service-icon ${service.severity?.toLowerCase()}`}>
            {service.severity === "High" ? "!" : "•"}
          </div>

          <div>
            <strong>
              Port {service.local_port} — {service.process_name || "Unknown"}
            </strong>
            <p>{service.message}</p>
            <small>
              {service.hostname || "N/A"} • {service.local_ip || "N/A"} •{" "}
              {formatDate(service.created_at)}
            </small>
          </div>
        </div>
      ))}
    </div>
  );
}

function RemoteConnections({ connections }) {
  if (!connections.length) {
    return <div className="empty-state">No remote connections detected.</div>;
  }

  return (
    <div className="remote-grid">
      {connections.slice(0, 10).map((connection, index) => (
        <div className="remote-card" key={index}>
          <div className="remote-top">
            <span className={`tag ${connection.severity?.toLowerCase()}`}>
              {connection.severity || "Low"}
            </span>
            <small>{connection.connection_count || 0} hit(s)</small>
          </div>

          <strong>{connection.remote_ip || "N/A"}</strong>
          <p>
            Port {connection.remote_port || "N/A"} •{" "}
            {connection.process_name || "Unknown"}
          </p>
        </div>
      ))}
    </div>
  );
}

function EventTable({ events }) {
  if (!events.length) {
    return <div className="empty-state">No recent network events found.</div>;
  }

  return (
    <div className="event-table-wrap">
      <table className="event-table">
        <thead>
          <tr>
            <th>Severity</th>
            <th>Host</th>
            <th>Event Type</th>
            <th>Local</th>
            <th>Remote</th>
            <th>Process</th>
            <th>Status</th>
            <th>Time</th>
          </tr>
        </thead>

        <tbody>
          {events.map((event) => (
            <tr key={event.id}>
              <td>
                <span className={`tag ${event.severity?.toLowerCase()}`}>
                  {event.severity || "Low"}
                </span>
              </td>
              <td>{event.hostname || "N/A"}</td>
              <td>{event.event_type || "Network Connection"}</td>
              <td>
                {event.local_ip || "N/A"}:{event.local_port || ""}
              </td>
              <td>
                {event.remote_ip
                  ? `${event.remote_ip}:${event.remote_port || ""}`
                  : "Listening / Local"}
              </td>
              <td>{event.process_name || "N/A"}</td>
              <td>{event.status || "N/A"}</td>
              <td>{formatDate(event.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildTrafficTrend(events) {
  const recent = events.slice(0, 30).reverse();
  const buckets = [];

  for (let i = 0; i < 6; i += 1) {
    buckets.push({
      label: `T-${5 - i}`,
      value: 0,
    });
  }

  recent.forEach((event, index) => {
    const bucketIndex = Math.min(Math.floor(index / 5), 5);
    buckets[bucketIndex].value += 1;
  });

  return buckets;
}

function buildPortChart(summary, events) {
  const map = new Map();

  const addPort = (port, label, severity) => {
    if (!port) return;

    const key = String(port);

    if (!map.has(key)) {
      map.set(key, {
        port: key,
        label: label || "Network activity",
        severity: severity || "Low",
        count: 0,
      });
    }

    map.get(key).count += 1;

    if (severity === "High") {
      map.get(key).severity = "High";
    } else if (severity === "Medium" && map.get(key).severity !== "High") {
      map.get(key).severity = "Medium";
    }
  };

  (summary?.sensitive_listening_services || []).forEach((service) => {
    addPort(
      service.local_port,
      service.process_name || "Listening service",
      service.severity || "Medium"
    );
  });

  events.forEach((event) => {
    addPort(
      event.remote_port || event.local_port,
      event.process_name || event.event_type,
      event.severity || "Low"
    );
  });

  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

function getHighestRisk(summary) {
  if ((summary?.high_alerts_24h || 0) > 0) return "High";
  if ((summary?.medium_alerts_24h || 0) > 0) return "Medium";
  return "Low";
}

function formatDate(value) {
  if (!value) return "N/A";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function NetworkStyles() {
  return (
    <style>
      {`
        .cm-network-page {
          min-height: 100vh;
          padding: 28px;
          color: #0f172a;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.16), transparent 30%),
            radial-gradient(circle at top right, rgba(34, 197, 94, 0.12), transparent 28%),
            #f8fafc;
          font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        .cm-hero {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 270px;
          gap: 22px;
          align-items: stretch;
          margin-bottom: 20px;
        }

        .cm-pill {
          display: inline-flex;
          padding: 8px 12px;
          border-radius: 999px;
          background: #dcfce7;
          color: #166534;
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 12px;
        }

        .cm-hero h1 {
          margin: 0;
          font-size: clamp(34px, 5vw, 56px);
          line-height: 0.98;
          letter-spacing: -0.06em;
        }

        .cm-hero p {
          max-width: 900px;
          margin: 14px 0 0;
          color: #64748b;
          font-size: 16px;
          line-height: 1.7;
        }

        .status-card,
        .metric-card,
        .panel,
        .loading-card {
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          box-shadow: 0 18px 50px rgba(15, 23, 42, 0.08);
        }

        .status-card {
          padding: 20px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .status-card h2 {
          margin: 18px 0 0;
          font-size: 48px;
          letter-spacing: -0.06em;
        }

        .status-card p {
          margin: 0 0 16px;
          color: #64748b;
        }

        .status-card button {
          border: 0;
          border-radius: 14px;
          background: #0f172a;
          color: white;
          font-weight: 900;
          padding: 12px 16px;
          cursor: pointer;
        }

        .risk-badge {
          width: fit-content;
          padding: 7px 11px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 900;
        }

        .risk-badge.high,
        .tag.high,
        .service-icon.high {
          background: #fee2e2;
          color: #991b1b;
        }

        .risk-badge.medium,
        .tag.medium,
        .service-icon.medium {
          background: #fef3c7;
          color: #92400e;
        }

        .risk-badge.low,
        .tag.low,
        .service-icon.low {
          background: #dcfce7;
          color: #166534;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 18px;
        }

        .metric-card {
          padding: 18px;
        }

        .metric-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .metric-head span {
          color: #64748b;
          font-size: 13px;
          font-weight: 900;
        }

        .metric-dot {
          width: 10px;
          height: 10px;
          border-radius: 99px;
          background: #2563eb;
        }

        .metric-dot.red { background: #ef4444; }
        .metric-dot.amber { background: #f59e0b; }
        .metric-dot.green { background: #22c55e; }
        .metric-dot.purple { background: #8b5cf6; }
        .metric-dot.blue { background: #2563eb; }

        .metric-card h2 {
          margin: 12px 0 4px;
          font-size: 36px;
          letter-spacing: -0.06em;
        }

        .metric-card p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.5;
        }

        .canonical-detection-panel {
          margin-bottom: 18px;
        }

        .canonical-detection-scope {
          display: grid;
          grid-template-columns: auto 1fr auto auto;
          gap: 10px 14px;
          align-items: center;
          margin-bottom: 16px;
          padding: 12px 14px;
          border-radius: 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        }

        .canonical-detection-scope span {
          color: #64748b;
          font-size: 12px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .canonical-detection-type {
          margin-top: 4px;
          color: #64748b;
          font-size: 11px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco,
            Consolas, "Liberation Mono", monospace;
        }

        .canonical-detection-note {
          margin-top: 14px;
          padding: 12px 14px;
          border-radius: 14px;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1e3a8a;
          font-size: 12px;
          line-height: 1.5;
          font-weight: 700;
        }

        .visual-grid-main {
          display: grid;
          grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.75fr);
          gap: 18px;
          margin-bottom: 18px;
        }

        .visual-grid-two {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          margin-bottom: 18px;
        }

        .panel {
          padding: 20px;
          overflow: hidden;
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 16px;
        }

        .panel-header h2 {
          margin: 0;
          font-size: 22px;
          letter-spacing: -0.04em;
        }

        .panel-header p {
          margin: 6px 0 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.5;
        }

        .live-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #16a34a;
          font-size: 13px;
          font-weight: 900;
        }

        .live-badge span {
          width: 10px;
          height: 10px;
          border-radius: 99px;
          background: #22c55e;
          animation: livePulse 1.4s infinite;
        }

        .network-map {
          position: relative;
          height: 500px;
          overflow: hidden;
          border-radius: 24px;
          background:
            radial-gradient(circle at center, rgba(34, 197, 94, 0.16), transparent 26%),
            linear-gradient(135deg, #020617, #0f172a);
          border: 1px solid rgba(148, 163, 184, 0.25);
        }

        .network-map::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(148, 163, 184, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148, 163, 184, 0.08) 1px, transparent 1px);
          background-size: 34px 34px;
          mask-image: radial-gradient(circle at center, black 0%, transparent 82%);
        }

        .map-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
        }

        .map-line {
          stroke: rgba(96, 165, 250, 0.55);
          stroke-width: 0.45;
          stroke-dasharray: 2 2;
          animation: dashMove 7s linear infinite;
        }

        .map-line.high {
          stroke: rgba(239, 68, 68, 0.95);
          stroke-width: 0.8;
        }

        .map-line.medium {
          stroke: rgba(245, 158, 11, 0.95);
          stroke-width: 0.65;
        }

        .map-line.low {
          stroke: rgba(34, 197, 94, 0.65);
        }

        .radar-ring {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 220px;
          height: 220px;
          border: 1px solid rgba(34, 197, 94, 0.35);
          border-radius: 999px;
          transform: translate(-50%, -50%);
          z-index: 2;
          animation: radarPulse 2.6s infinite;
        }

        .ring-two {
          animation-delay: 1.2s;
        }

        .center-device {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 165px;
          height: 165px;
          transform: translate(-50%, -50%);
          border-radius: 999px;
          z-index: 5;
          background:
            radial-gradient(circle at top, #22c55e, #2563eb 42%, #0f172a 100%);
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow: 0 0 60px rgba(34, 197, 94, 0.35);
          color: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 18px;
        }

        .center-device span {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 900;
          opacity: 0.85;
        }

        .center-device strong {
          margin-top: 8px;
          font-size: 18px;
          line-height: 1.1;
          word-break: break-word;
        }

        .network-node {
          position: absolute;
          transform: translate(-50%, -50%);
          z-index: 4;
          width: 160px;
          padding: 12px;
          border-radius: 18px;
          background: rgba(15, 23, 42, 0.88);
          border: 1px solid rgba(148, 163, 184, 0.3);
          color: white;
          box-shadow: 0 18px 35px rgba(0, 0, 0, 0.28);
          backdrop-filter: blur(12px);
        }

        .network-node.high {
          border-color: rgba(239, 68, 68, 0.9);
        }

        .network-node.medium {
          border-color: rgba(245, 158, 11, 0.95);
        }

        .network-node.low {
          border-color: rgba(34, 197, 94, 0.75);
        }

        .network-node span {
          display: inline-flex;
          padding: 4px 8px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.1);
          color: #cbd5e1;
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .network-node strong {
          display: block;
          font-size: 14px;
          margin-bottom: 4px;
          word-break: break-word;
        }

        .network-node small {
          color: #cbd5e1;
          font-size: 11px;
          line-height: 1.35;
          word-break: break-word;
        }

        .empty-map {
          position: absolute;
          z-index: 6;
          bottom: 28px;
          left: 50%;
          transform: translateX(-50%);
          color: #cbd5e1;
          text-align: center;
          max-width: 360px;
        }

        .donut-wrap {
          display: grid;
          place-items: center;
          padding: 14px 0 20px;
        }

        .donut-chart {
          width: 230px;
          height: 230px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
        }

        .donut-hole {
          width: 138px;
          height: 138px;
          border-radius: 999px;
          background: white;
          display: grid;
          place-items: center;
          align-content: center;
          box-shadow: 0 14px 35px rgba(15, 23, 42, 0.12);
        }

        .donut-hole strong {
          font-size: 36px;
          letter-spacing: -0.06em;
        }

        .donut-hole span {
          color: #64748b;
          font-size: 13px;
          font-weight: 900;
        }

        .severity-legend {
          display: grid;
          gap: 10px;
        }

        .legend-item {
          display: grid;
          grid-template-columns: 12px 1fr auto;
          gap: 10px;
          align-items: center;
          padding: 12px;
          border-radius: 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
        }

        .legend-item p {
          margin: 0;
          color: #334155;
          font-weight: 800;
        }

        .legend-dot {
          width: 11px;
          height: 11px;
          border-radius: 99px;
        }

        .legend-dot.red { background: #ef4444; }
        .legend-dot.amber { background: #f59e0b; }
        .legend-dot.green { background: #22c55e; }

        .last-updated {
          margin-top: 14px;
          color: #64748b;
          font-size: 13px;
        }

        .line-chart-wrap {
          height: 260px;
          position: relative;
          background:
            linear-gradient(#f1f5f9 1px, transparent 1px),
            linear-gradient(90deg, #f1f5f9 1px, transparent 1px);
          background-size: 40px 40px;
          border-radius: 18px;
          border: 1px solid #e2e8f0;
          padding: 14px;
        }

        .line-chart {
          width: 100%;
          height: 210px;
          overflow: visible;
        }

        .line-path {
          fill: none;
          stroke: #2563eb;
          stroke-width: 2.4;
          vector-effect: non-scaling-stroke;
        }

        .line-point {
          fill: #22c55e;
          stroke: white;
          stroke-width: 0.8;
          vector-effect: non-scaling-stroke;
        }

        .chart-labels {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          color: #64748b;
          font-size: 11px;
          font-weight: 800;
          text-align: center;
        }

        .bar-list {
          display: grid;
          gap: 12px;
        }

        .bar-row {
          display: grid;
          grid-template-columns: 115px 1fr 36px;
          gap: 12px;
          align-items: center;
        }

        .bar-label strong {
          display: block;
          font-size: 14px;
        }

        .bar-label span {
          color: #64748b;
          font-size: 11px;
        }

        .bar-track {
          height: 14px;
          border-radius: 999px;
          overflow: hidden;
          background: #e2e8f0;
        }

        .bar-fill {
          height: 100%;
          border-radius: 999px;
          background: #2563eb;
        }

        .bar-fill.high { background: #ef4444; }
        .bar-fill.medium { background: #f59e0b; }
        .bar-fill.low { background: #22c55e; }

        .bar-count {
          color: #334155;
          font-weight: 900;
          text-align: right;
        }

        .service-list {
          display: grid;
          gap: 12px;
        }

        .service-item {
          display: grid;
          grid-template-columns: 38px 1fr;
          gap: 12px;
          padding: 14px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
        }

        .service-icon {
          width: 34px;
          height: 34px;
          display: grid;
          place-items: center;
          border-radius: 999px;
          font-weight: 1000;
        }

        .service-item strong {
          display: block;
          margin-bottom: 5px;
        }

        .service-item p {
          margin: 0 0 6px;
          color: #475569;
          font-size: 13px;
          line-height: 1.45;
        }

        .service-item small {
          color: #64748b;
        }

        .remote-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
        }

        .remote-card {
          padding: 14px;
          border-radius: 18px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
        }

        .remote-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .remote-card strong {
          display: block;
          word-break: break-word;
          margin-bottom: 4px;
        }

        .remote-card p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
        }

        .tag {
          display: inline-flex;
          padding: 5px 9px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 900;
          white-space: nowrap;
        }

        .event-table-wrap {
          width: 100%;
          overflow-x: auto;
        }

        .event-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 1100px;
        }

        .event-table th {
          background: #f1f5f9;
          color: #334155;
          padding: 12px;
          text-align: left;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 1px solid #e2e8f0;
        }

        .event-table td {
          padding: 12px;
          color: #334155;
          font-size: 13px;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: top;
        }

        .empty-state {
          padding: 18px;
          border-radius: 18px;
          background: #f8fafc;
          border: 1px dashed #cbd5e1;
          color: #64748b;
          text-align: center;
        }

        .error-box {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
          border-radius: 18px;
          padding: 14px 16px;
          margin-bottom: 18px;
        }

        .loading-card {
          max-width: 580px;
          margin: 80px auto;
          padding: 32px;
          text-align: center;
        }

        .loading-pulse {
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #22c55e;
          margin: 0 auto 18px;
          animation: livePulse 1.4s infinite;
        }

        @keyframes livePulse {
          0% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.35);
          }
          100% {
            box-shadow: 0 0 0 13px rgba(34, 197, 94, 0);
          }
        }

        @keyframes radarPulse {
          0% {
            transform: translate(-50%, -50%) scale(0.65);
            opacity: 0.85;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.25);
            opacity: 0;
          }
        }

        @keyframes dashMove {
          to {
            stroke-dashoffset: -40;
          }
        }

        @media (max-width: 1250px) {
          .metrics-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .visual-grid-main,
          .visual-grid-two {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 760px) {
          .cm-network-page {
            padding: 18px;
          }

          .cm-hero {
            grid-template-columns: 1fr;
          }

          .metrics-grid {
            grid-template-columns: 1fr 1fr;
          }

          .network-map {
            height: 650px;
          }

          .network-node {
            width: 135px;
          }
        }

        @media (max-width: 520px) {
          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .network-map {
            height: 730px;
          }

          .center-device {
            width: 140px;
            height: 140px;
          }

          .center-device strong {
            font-size: 15px;
          }
        }
      `}
    </style>
  );
}

export default NetworkProtection;